<script>
import { useAlert } from 'dashboard/composables';
import { mapActions } from 'vuex';
import { FIELD_CLASS } from '../constants';
import {
  apiErrorCode,
  apiErrorMessage,
} from '../../../../api/realEstate/errors';
import { brochureName, isImageBrochure } from '../helpers';

// A project's brochures (docs/real-estate/be/13-fe-update-2026-09-30.md §1):
// PDF, JPG or PNG up to 10 MB, each with an optional title. Same flow as
// GalleryModal — every row action (rename, reorder, remove) and each upload
// saves straight away, and the project detail is re-read after each one.
const ALLOWED_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/jpg',
  'image/png',
];
const MAX_SIZE_BYTES = 10 * 1024 * 1024;
const TITLE_MAX = 100;

export default {
  name: 'BrochuresModal',
  props: {
    project: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      fieldClass: FIELD_CLASS,
      titleMax: TITLE_MAX,
      newTitle: '',
      isUploading: false,
    };
  },
  computed: {
    brochures() {
      return this.project.brochures || [];
    },
  },
  methods: {
    ...mapActions('realEstate', [
      'addProjectBrochure',
      'renameProjectBrochure',
      'reorderProjectBrochures',
      'removeProjectBrochure',
    ]),
    brochureName,
    isImageBrochure,
    // A brochure-worded message for 413/415 (the shared file-size copy
    // names image types only); a reorder that raced another change reloads.
    errorMessage(err, fallbackKey) {
      const code = apiErrorCode(err);
      if (code === 'file_too_large' || code === 'unsupported_brochure_type') {
        return this.$t('REAL_ESTATE.ERRORS.UNSUPPORTED_BROCHURE_TYPE');
      }
      return apiErrorMessage(this, err, fallbackKey);
    },
    pickFile() {
      this.$refs.fileInput.click();
    },
    async handleFileChange(event) {
      const file = event.target.files[0];
      // Reset so choosing the same file twice in a row still fires change.
      event.target.value = '';
      if (!file) return;
      if (!ALLOWED_TYPES.includes(file.type) || file.size > MAX_SIZE_BYTES) {
        useAlert(this.$t('REAL_ESTATE.ERRORS.UNSUPPORTED_BROCHURE_TYPE'));
        return;
      }
      this.isUploading = true;
      try {
        await this.addProjectBrochure({
          propertyId: this.project.property_id,
          file,
          title: this.newTitle.trim(),
        });
        this.newTitle = '';
        useAlert(this.$t('REAL_ESTATE.BROCHURES.SUCCESS_ADDED'));
      } catch (err) {
        useAlert(this.errorMessage(err, 'REAL_ESTATE.BROCHURES.ERROR_ADD'));
        // eslint-disable-next-line no-console
        console.error(err);
      } finally {
        this.isUploading = false;
      }
    },
    async handleTitleChange(brochure, value) {
      const title = value.trim();
      if (title === (brochure.title || '')) return;
      try {
        await this.renameProjectBrochure({
          propertyId: this.project.property_id,
          brochureId: brochure.brochure_id,
          title: title || null,
        });
        useAlert(this.$t('REAL_ESTATE.BROCHURES.SUCCESS_RENAMED'));
      } catch (err) {
        useAlert(this.errorMessage(err, 'REAL_ESTATE.BROCHURES.ERROR_RENAME'));
        // eslint-disable-next-line no-console
        console.error(err);
      }
    },
    async handleMove(index, direction) {
      const target = index + direction;
      if (target < 0 || target >= this.brochures.length) return;
      const reordered = [...this.brochures];
      [reordered[index], reordered[target]] = [
        reordered[target],
        reordered[index],
      ];
      try {
        await this.reorderProjectBrochures({
          propertyId: this.project.property_id,
          brochureIds: reordered.map(b => b.brochure_id),
        });
        useAlert(this.$t('REAL_ESTATE.BROCHURES.SUCCESS_REORDERED'));
      } catch (err) {
        useAlert(this.errorMessage(err, 'REAL_ESTATE.BROCHURES.ERROR_REORDER'));
        // eslint-disable-next-line no-console
        console.error(err);
      }
    },
    async handleRemove(brochure) {
      try {
        await this.removeProjectBrochure({
          propertyId: this.project.property_id,
          brochureId: brochure.brochure_id,
        });
        useAlert(this.$t('REAL_ESTATE.BROCHURES.SUCCESS_REMOVED'));
      } catch (err) {
        useAlert(this.errorMessage(err, 'REAL_ESTATE.BROCHURES.ERROR_REMOVE'));
        // eslint-disable-next-line no-console
        console.error(err);
      }
    },
  },
};
</script>

<template>
  <div
    class="fixed inset-0 flex items-center justify-center bg-slate-800/80 z-50"
  >
    <div
      class="bg-white dark:bg-slate-900 rounded-lg p-6 shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto"
    >
      <div class="flex items-center mb-4 justify-between">
        <h1 class="text-xl font-bold text-slate-900 dark:text-slate-100">
          {{ $t('REAL_ESTATE.BROCHURES.MODAL_TITLE') }}
        </h1>
        <woot-button
          variant="clear"
          color-scheme="alert"
          icon="dismiss"
          @click="$emit('close')"
        />
      </div>

      <div class="space-y-2 mb-4">
        <div
          v-for="(brochure, index) in brochures"
          :key="brochure.brochure_id"
          class="flex items-center gap-3 border border-slate-200 dark:border-slate-700 rounded-lg p-2"
        >
          <a
            :href="brochure.url"
            target="_blank"
            rel="noopener noreferrer"
            :title="
              $t('REAL_ESTATE.BROCHURES.OPEN', { name: brochureName(brochure) })
            "
            class="relative w-14 h-14 shrink-0 rounded-md overflow-hidden border border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-400"
          >
            <fluent-icon
              :icon="isImageBrochure(brochure) ? 'image' : 'document'"
              size="18"
            />
            <img
              v-if="isImageBrochure(brochure)"
              :src="brochure.url"
              alt=""
              class="absolute inset-0 w-full h-full object-cover"
              @error="$event.target.remove()"
            />
          </a>
          <div class="flex-1 min-w-0">
            <input
              :value="brochure.title"
              type="text"
              :placeholder="brochure.file_name"
              :maxlength="titleMax"
              :class="fieldClass"
              class="!py-1.5 reset-base mb-0"
              @change="handleTitleChange(brochure, $event.target.value)"
            />
            <p class="mt-1 mb-0 text-xs text-slate-400 truncate">
              {{ brochure.file_name }}
            </p>
          </div>
          <div class="flex items-center gap-1 shrink-0">
            <woot-button
              variant="clear"
              size="small"
              icon="chevron-up"
              :is-disabled="index === 0"
              :title="$t('REAL_ESTATE.BROCHURES.MOVE_UP')"
              @click="handleMove(index, -1)"
            />
            <woot-button
              variant="clear"
              size="small"
              icon="chevron-down"
              :is-disabled="index === brochures.length - 1"
              :title="$t('REAL_ESTATE.BROCHURES.MOVE_DOWN')"
              @click="handleMove(index, 1)"
            />
            <woot-button
              variant="clear"
              size="small"
              color-scheme="alert"
              icon="delete"
              :title="$t('REAL_ESTATE.BROCHURES.REMOVE')"
              @click="handleRemove(brochure)"
            />
          </div>
        </div>
        <p v-if="!brochures.length" class="text-sm text-slate-400">
          {{ $t('REAL_ESTATE.BROCHURES.EMPTY_MODAL') }}
        </p>
      </div>

      <div class="pt-3 border-t border-slate-200 dark:border-slate-700">
        <label class="block mb-1 text-sm text-slate-700 dark:text-slate-200">
          {{ $t('REAL_ESTATE.BROCHURES.ADD_LABEL') }}
        </label>
        <div class="flex items-center gap-2">
          <input
            v-model="newTitle"
            type="text"
            :maxlength="titleMax"
            :placeholder="$t('REAL_ESTATE.BROCHURES.TITLE_PLACEHOLDER')"
            :class="fieldClass"
            class="flex-1 min-w-0 reset-base mb-0"
          />
          <woot-button
            type="button"
            variant="smooth"
            size="small"
            icon="upload"
            :is-loading="isUploading"
            class="shrink-0"
            @click="pickFile"
          >
            {{ $t('REAL_ESTATE.BROCHURES.ADD_BUTTON') }}
          </woot-button>
        </div>
        <p class="mt-1 mb-0 text-xs text-slate-400">
          {{ $t('REAL_ESTATE.BROCHURES.HINT') }}
        </p>
        <input
          ref="fileInput"
          type="file"
          accept="application/pdf,image/jpeg,image/jpg,image/png"
          class="hidden"
          @change="handleFileChange"
        />
      </div>

      <div
        class="flex justify-end gap-3 pt-4 mt-2 border-t border-slate-200 dark:border-slate-700"
      >
        <woot-button type="button" @click="$emit('close')">
          {{ $t('REAL_ESTATE.BROCHURES.DONE') }}
        </woot-button>
      </div>
    </div>
  </div>
</template>
